import type { ProjectListItem } from '@atlas/api-contract';
import { Loader2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

import { toDisplayableError } from '@/components/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useDeleteProject } from './projects-queries';

export interface DeleteProjectDialogProps {
  /** The project queued for deletion, or `null` when none is. */
  project: ProjectListItem | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Delete a project and everything under it.
 *
 * The delete cascades through prototypes, versions, threads and comments — work
 * by people who are not in the room — and there is no undo and no soft delete.
 * So the confirmation is the project's own name, typed out: it cannot be
 * satisfied by muscle memory, and typing it means reading it.
 *
 * The consequences are spelled out in full rather than summarised as
 * "permanent", because "permanent" does not tell anyone that a colleague's
 * comment threads go with it.
 */
export const DeleteProjectDialog = ({
  project,
  onOpenChange,
}: DeleteProjectDialogProps) => {
  const deleteProject = useDeleteProject();
  const confirmationId = useId();
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const open = project !== null;

  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError(null);
    }
  }, [open]);

  const canDelete =
    project !== null && confirmation.trim() === project.name && !deleteProject.isPending;

  const handleDelete = async () => {
    if (project === null) {
      return;
    }

    setError(null);

    try {
      await deleteProject.mutateAsync(project.id);
      toast.success(`Deleted “${project.name}”.`);
      onOpenChange(false);
    } catch (caught: unknown) {
      setError(toDisplayableError(caught));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && deleteProject.isPending) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete “{project?.name ?? ''}”?</DialogTitle>
          <DialogDescription>
            This deletes the project and everything inside it: every prototype,
            every uploaded version of those prototypes, and every comment thread
            and reply left on them, by anyone. It cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {project === null ? null : (
          <div className="space-y-2">
            <Label htmlFor={confirmationId}>
              Type{' '}
              <span className="font-semibold text-foreground">{project.name}</span>{' '}
              to confirm
            </Label>
            <Input
              id={confirmationId}
              autoComplete="off"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
              placeholder={project.name}
            />
            <p className="text-xs text-muted-foreground">
              {project.prototypeCount}{' '}
              {project.prototypeCount === 1 ? 'prototype' : 'prototypes'} will be
              destroyed with it.
            </p>
          </div>
        )}

        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={deleteProject.isPending}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canDelete}
            onClick={() => {
              void handleDelete();
            }}
          >
            {deleteProject.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="animate-spin" />
                Deleting…
              </>
            ) : (
              'Delete project'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
